import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, AlertCircle, XCircle, Clock, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CheckItem {
  id: string;
  name: string;
  description: string;
  category: 'stripe' | 'security' | 'functionality' | 'performance';
  status: 'pending' | 'running' | 'passed' | 'failed' | 'warning';
  details?: string;
  critical: boolean;
}

const ProductionReadinessChecker: React.FC = () => {
  const [checks, setChecks] = useState<CheckItem[]>([
    {
      id: 'stripe-live-mode',
      name: 'Stripe Live Mode Configuration',
      description: 'Verify Stripe is configured for live payments',
      category: 'stripe',
      status: 'pending',
      critical: true
    },
    {
      id: 'stripe-webhook',
      name: 'Stripe Webhook Connectivity',
      description: 'Test webhook endpoint accessibility',
      category: 'stripe',
      status: 'pending',
      critical: true
    },
    {
      id: 'payment-flow',
      name: 'End-to-End Payment Flow',
      description: 'Test complete subscription creation flow',
      category: 'stripe',
      status: 'pending',
      critical: true
    },
    {
      id: 'rls-security',
      name: 'Row Level Security Policies',
      description: 'Verify all RLS policies are properly configured',
      category: 'security',
      status: 'pending',
      critical: true
    },
    {
      id: 'email-notifications',
      name: 'Email Notification System',
      description: 'Test referral and system email notifications',
      category: 'functionality',
      status: 'pending',
      critical: true
    },
    {
      id: 'user-journeys',
      name: 'Complete User Journeys',
      description: 'Test provider and case manager workflows',
      category: 'functionality',
      status: 'pending',
      critical: true
    },
    {
      id: 'ssl-security',
      name: 'SSL/HTTPS Configuration',
      description: 'Verify secure connection and certificates',
      category: 'security',
      status: 'pending',
      critical: true
    },
    {
      id: 'performance-load',
      name: 'Performance Under Load',
      description: 'Test response times and concurrent users',
      category: 'performance',
      status: 'pending',
      critical: false
    }
  ]);

  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();

  const runSingleCheck = async (check: CheckItem): Promise<CheckItem> => {
    const updatedCheck = { ...check, status: 'running' as const };
    
    try {
      switch (check.id) {
        case 'stripe-live-mode':
          // Check if Stripe is in live mode by testing the checkout creation
          const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('create-checkout', {
            body: { tier: 'tier1', testMode: true }
          });
          
          if (checkoutError) {
            return { ...updatedCheck, status: 'failed', details: `Stripe configuration error: ${checkoutError.message}` };
          }
          
          if (checkoutData?.url && !checkoutData.url.includes('test')) {
            return { ...updatedCheck, status: 'passed', details: 'Stripe live mode is properly configured' };
          } else {
            return { ...updatedCheck, status: 'warning', details: 'Stripe appears to be in test mode' };
          }

        case 'stripe-webhook':
          // Test webhook endpoint
          const { data: webhookData, error: webhookError } = await supabase.functions.invoke('system-monitoring', {
            body: { check: 'webhook-connectivity' }
          });
          
          return webhookError 
            ? { ...updatedCheck, status: 'failed', details: `Webhook test failed: ${webhookError.message}` }
            : { ...updatedCheck, status: 'passed', details: 'Webhook endpoint is accessible' };

        case 'payment-flow':
          // Test payment processing capability (without actual charge)
          const { error: paymentError } = await supabase.functions.invoke('check-subscription');
          
          return paymentError
            ? { ...updatedCheck, status: 'failed', details: `Payment flow error: ${paymentError.message}` }
            : { ...updatedCheck, status: 'passed', details: 'Payment flow is operational' };

        case 'rls-security':
          // Test RLS policies
          const { data: profileData, error: rlsError } = await supabase
            .from('profiles')
            .select('id')
            .limit(1);
          
          return rlsError
            ? { ...updatedCheck, status: 'failed', details: `RLS policy error: ${rlsError.message}` }
            : { ...updatedCheck, status: 'passed', details: 'RLS policies are functioning correctly' };

        case 'email-notifications':
          // Test email system
          const { error: emailError } = await supabase.functions.invoke('send-notification', {
            body: { 
              type: 'system_test',
              recipient_email: 'test@carekonnect.com',
              test: true 
            }
          });
          
          return emailError
            ? { ...updatedCheck, status: 'failed', details: `Email system error: ${emailError.message}` }
            : { ...updatedCheck, status: 'passed', details: 'Email notification system is working' };

        case 'user-journeys':
          // Test critical database operations
          const { error: journeyError } = await supabase
            .from('bed_listings')
            .select('id')
            .limit(1);
          
          return journeyError
            ? { ...updatedCheck, status: 'failed', details: `User journey test failed: ${journeyError.message}` }
            : { ...updatedCheck, status: 'passed', details: 'Core user journeys are accessible' };

        case 'ssl-security':
          // Check if we're running on HTTPS
          const isSecure = window.location.protocol === 'https:';
          
          return isSecure
            ? { ...updatedCheck, status: 'passed', details: 'SSL/HTTPS is properly configured' }
            : { ...updatedCheck, status: 'warning', details: 'Running on HTTP - ensure HTTPS in production' };

        case 'performance-load':
          // Simple performance check
          const startTime = performance.now();
          await supabase.from('profiles').select('count').limit(1).single();
          const endTime = performance.now();
          const responseTime = endTime - startTime;
          
          return responseTime < 1000
            ? { ...updatedCheck, status: 'passed', details: `Response time: ${responseTime.toFixed(0)}ms` }
            : { ...updatedCheck, status: 'warning', details: `Slow response time: ${responseTime.toFixed(0)}ms` };

        default:
          return { ...updatedCheck, status: 'failed', details: 'Unknown check type' };
      }
    } catch (error) {
      return { 
        ...updatedCheck, 
        status: 'failed', 
        details: `Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}` 
      };
    }
  };

  const runAllChecks = async () => {
    setIsRunning(true);
    setProgress(0);
    
    const totalChecks = checks.length;
    const updatedChecks = [...checks];
    
    for (let i = 0; i < checks.length; i++) {
      const check = checks[i];
      const result = await runSingleCheck(check);
      
      updatedChecks[i] = result;
      setChecks([...updatedChecks]);
      setProgress(((i + 1) / totalChecks) * 100);
      
      // Small delay to show progress
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setIsRunning(false);
    
    const criticalFailures = updatedChecks.filter(c => c.critical && c.status === 'failed').length;
    const totalPassed = updatedChecks.filter(c => c.status === 'passed').length;
    
    if (criticalFailures === 0) {
      toast({
        title: "Production Readiness Check Complete",
        description: `${totalPassed}/${totalChecks} checks passed. System is ready for production!`,
      });
    } else {
      toast({
        title: "Critical Issues Found",
        description: `${criticalFailures} critical issues must be resolved before production deployment.`,
        variant: "destructive",
      });
    }
  };

  const getStatusIcon = (status: CheckItem['status']) => {
    switch (status) {
      case 'passed': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'failed': return <XCircle className="h-5 w-5 text-red-600" />;
      case 'warning': return <AlertCircle className="h-5 w-5 text-yellow-600" />;
      case 'running': return <Clock className="h-5 w-5 text-blue-600 animate-spin" />;
      default: return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: CheckItem['status'], critical: boolean) => {
    switch (status) {
      case 'passed': return <Badge className="bg-green-100 text-green-800">Passed</Badge>;
      case 'failed': return <Badge className="bg-red-100 text-red-800">{critical ? 'Critical' : 'Failed'}</Badge>;
      case 'warning': return <Badge className="bg-yellow-100 text-yellow-800">Warning</Badge>;
      case 'running': return <Badge className="bg-blue-100 text-blue-800">Running</Badge>;
      default: return <Badge className="bg-gray-100 text-gray-800">Pending</Badge>;
    }
  };

  const criticalIssues = checks.filter(c => c.critical && c.status === 'failed').length;
  const totalPassed = checks.filter(c => c.status === 'passed').length;
  const readyForProduction = criticalIssues === 0 && totalPassed > 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Production Readiness Checker
            {readyForProduction && <CheckCircle className="h-6 w-6 text-green-600" />}
          </CardTitle>
          <CardDescription>
            Comprehensive validation for production deployment
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <Button 
              onClick={runAllChecks} 
              disabled={isRunning}
              className="flex-1"
            >
              {isRunning ? 'Running Checks...' : 'Run All Checks'}
            </Button>
          </div>
          
          {isRunning && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{totalPassed}</div>
              <div className="text-green-700">Passed</div>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{checks.filter(c => c.status === 'failed').length}</div>
              <div className="text-red-700">Failed</div>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{checks.filter(c => c.status === 'warning').length}</div>
              <div className="text-yellow-700">Warnings</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{criticalIssues}</div>
              <div className="text-blue-700">Critical Issues</div>
            </div>
          </div>

          {readyForProduction && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-800 font-medium">
                <CheckCircle className="h-5 w-5" />
                System Ready for Production Deployment
              </div>
              <p className="text-green-700 text-sm mt-1">
                All critical checks have passed. The platform is ready for live deployment.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="space-y-4">
        {['stripe', 'security', 'functionality', 'performance'].map(category => (
          <Card key={category}>
            <CardHeader>
              <CardTitle className="text-lg capitalize">{category} Checks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {checks.filter(check => check.category === category).map(check => (
                  <div key={check.id} className="flex items-start gap-3 p-3 border rounded-lg">
                    {getStatusIcon(check.status)}
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{check.name}</span>
                        {getStatusBadge(check.status, check.critical)}
                        {check.critical && <Badge variant="outline">Critical</Badge>}
                      </div>
                      <p className="text-sm text-gray-600">{check.description}</p>
                      {check.details && (
                        <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                          {check.details}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Next Steps for Production Deployment</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2 text-sm">
            <p className="font-medium">If all checks pass:</p>
            <ol className="list-decimal list-inside space-y-1 ml-4">
              <li>Verify domain configuration and SSL certificates</li>
              <li>Set up production monitoring and alerting</li>
              <li>Configure backup and disaster recovery</li>
              <li>Update Stripe webhook URLs to production domain</li>
              <li>Test with real payment methods (small amounts)</li>
              <li>Set up customer support processes</li>
              <li>Go live! 🚀</li>
            </ol>
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button variant="outline" size="sm" asChild>
              <a href="https://supabase.com/dashboard/project/soueyhxwnjikppvwavfd" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Supabase Dashboard
              </a>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Stripe Dashboard
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductionReadinessChecker;